/**
 * Collection chrome (CB-5a) — structure only: the shell's toolbar, chip row,
 * scroller, list element, empty state and selection bar. NO listeners, NO
 * timers — the orchestrator (collection-browser.js) wires behaviour, which is
 * what keeps this file trivially jsdom-testable and the wiring in one place.
 *
 * DOM-first like the item view: every caller string lands via textContent.
 */

import { createButton } from './components.js';
import { iconEl } from './icons.js';
import { illustration } from './illustrations.js';
import { slugToLabel } from '../shared/collection-chips.js';

/**
 * @param {{
 *   layout?: 'grid'|'rows',
 *   listTag?: string, listRole?: string|null, listAriaLabel?: string,
 *   search?: {placeholder?: string, ariaLabel?: string, icon?: string|false,
 *     wrapClassName?: string}|false,
 *   chips?: object|false,
 *   headerMeta?: boolean,
 *   selection?: boolean
 * }} opts
 */
export function buildCollectionShell(opts = {}) {
  const {
    layout = 'grid',
    listTag = 'ul',
    listRole = null,
    listAriaLabel,
    search = {},
    chips = false,
    headerMeta = true,
    /** Right-hand toolbar control — an element or a factory. See below. */
    toolbarTrailing = null,
    selection = false,
    density = 'default',
    // The floating bar and its reserved strip are a property of the selection
    // MODE, not of selection itself: a single-select list (CB-10) never has a
    // bulk action to offer and never has an empty selection to announce.
    selectionBar = selection
  } = opts;

  const shell = document.createElement('div');
  shell.className = 'pb-coll-shell';

  let searchInput = null;
  let metaEl = null;
  let trailingEl = null;
  if (search !== false || headerMeta) {
    const toolbar = document.createElement('div');
    toolbar.className = 'pb-coll-toolbar';
    if (search !== false) {
      searchInput = document.createElement('input');
      searchInput.type = 'search';
      searchInput.className = 'pb-coll-search';
      searchInput.placeholder = search.placeholder || 'Search…';
      searchInput.setAttribute('aria-label', search.ariaLabel || search.placeholder || 'Search');

      // MK-1b: the leading glyph. The kit shipped a bare input, and the two
      // surfaces that wanted an icon each re-dressed THIS node by hand
      // afterwards — `palette.js` wrapping it in `.pb-tpl-v2-search`,
      // `fleet-modal.js` stripping `.pb-coll-search` off it and re-parenting it
      // into `.pb-fleet-search` — which is two implementations of one idea, and
      // is how one of them ended up calling `icon('search', { size: 16 })` with
      // a number where the size ramp expects `'sm'`, silently rendering `base`.
      //
      // A `<label>` rather than a `<div>`: clicking the glyph then focuses the
      // field, which a div cannot do without a listener.
      const glyph = search.icon === false ? null : iconEl(search.icon || 'search');
      if (glyph) {
        glyph.classList.add('pb-coll-search-icon');
        const wrap = document.createElement('label');
        wrap.className = ['pb-coll-search-wrap', search.wrapClassName].filter(Boolean).join(' ');
        wrap.append(glyph, searchInput);
        toolbar.appendChild(wrap);
      } else {
        toolbar.appendChild(searchInput);
      }
    }
    if (headerMeta) {
      metaEl = document.createElement('span');
      metaEl.className = 'pb-coll-meta';
      // The meta span is a live region ONLY when selection is off — the
      // selection live region below would otherwise double-announce one state
      // (palette sets it unconditionally today; the module must not).
      if (!selection) {
        metaEl.setAttribute('role', 'status');
        metaEl.setAttribute('aria-live', 'polite');
      }
      toolbar.appendChild(metaEl);
    }
    // The right-hand slot, LAST in the row. A surface with a second axis (a
    // sort order, a source) puts its control here rather than in the chip row:
    // the chip row is the primary facet, and a control parked at its end
    // competes with it and takes its own line the moment the chips wrap.
    // Built once and appended once — nothing repaints this row.
    if (toolbarTrailing) {
      const el = typeof toolbarTrailing === 'function' ? toolbarTrailing() : toolbarTrailing;
      if (el) {
        trailingEl = document.createElement('div');
        trailingEl.className = 'pb-coll-toolbar-trailing';
        trailingEl.appendChild(el);
        toolbar.appendChild(trailingEl);
      }
    }
    shell.appendChild(toolbar);
  }

  let chipRow = null;
  let drawerEl = null;
  if (chips !== false) {
    chipRow = document.createElement('div');
    chipRow.className = 'pb-coll-chips';
    chipRow.setAttribute('role', 'group');
    chipRow.setAttribute('aria-label', chips.ariaLabel || 'Filters');
    shell.appendChild(chipRow);

    drawerEl = document.createElement('div');
    drawerEl.className = 'pb-coll-drawer';
    drawerEl.hidden = true;
    shell.appendChild(drawerEl);
  }

  const scroller = document.createElement('div');
  // The reserve class is stamped once, here, never toggled — see the rule's
  // comment in collection.css. No bar ⇒ no strip to reserve.
  scroller.className = selectionBar ? 'pb-coll-scroll pb-coll-scroll-reserve' : 'pb-coll-scroll';

  const listEl = document.createElement(listTag);
  listEl.className = layout === 'rows' ? 'pb-coll-rows' : 'pb-coll-grid';
  // Density is a GRID property, not a layout: same rule shape, a smaller track
  // token (CB-11's icon library against CB-9's poster library).
  if (layout !== 'rows' && density === 'dense') listEl.classList.add('pb-coll-grid-dense');
  if (layout !== 'rows' && density === 'wide') listEl.classList.add('pb-coll-grid-wide');
  if (layout !== 'rows' && density === 'media') listEl.classList.add('pb-coll-grid-media');
  // SS-6: 'strip' — ONE full-width column, for records whose art is a wide
  // strip and whose legibility is a function of card WIDTH (see
  // .pb-coll-grid-strip in collection.css).
  if (layout !== 'rows' && density === 'strip') listEl.classList.add('pb-coll-grid-strip');
  // listRole is EXPLICIT ('list' | 'listbox'), never defaulted — a <ul>
  // carries its implicit role and anything else is the caller's statement.
  if (listRole) listEl.setAttribute('role', listRole);
  if (listAriaLabel) listEl.setAttribute('aria-label', listAriaLabel);
  scroller.appendChild(listEl);

  const emptyEl = document.createElement('div');
  emptyEl.className = 'pb-coll-empty';
  emptyEl.hidden = true;
  scroller.appendChild(emptyEl);

  shell.appendChild(scroller);

  let selbar = null;
  /**
   * MK-7: the live region is UNCONDITIONAL now.
   *
   * It used to be created only alongside a selection model, because selection
   * announcements were the first thing that needed one. That coupling is wrong
   * for a searchable list: a surface with no selection still filters, still
   * empties, still hides records behind a disclosure — and every one of those
   * is a state change a screen-reader user is entitled to hear. The image
   * picker has no selection at all, so it announced nothing.
   *
   * `.pb-sr-only` and an empty textContent, so a surface that never announces
   * renders exactly as it did.
   */
  const liveRegion = document.createElement('span');
  liveRegion.className = 'pb-sr-only';
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  shell.appendChild(liveRegion);
  if (selection) {
    if (selectionBar) {
      selbar = document.createElement('div');
      selbar.className = 'pb-coll-selbar';
      selbar.hidden = true;
      const count = document.createElement('span');
      count.className = 'pb-coll-selbar-count';
      selbar.appendChild(count);
    }
  }

  return {
    shell,
    searchInput,
    metaEl,
    chipRow,
    drawerEl,
    scroller,
    listEl,
    emptyEl,
    trailingEl,
    selbar,
    liveRegion
  };
}

/**
 * Paint the chip row from a buildChipModel() result. Structure only — the
 * orchestrator delegates clicks off `[data-cb-chip]`.
 *
 * `trailing` is an OWNED element parked at the end of the row (the template
 * selector's "Built by" select). It exists because this function starts with
 * `chipRow.textContent = ''`: a surface that appended its own control to the
 * row would have it deleted by the next repaint — a chip click, a favourites
 * change, a fresh catalog response — with nothing to say why. Re-appending the
 * SAME node keeps its listeners and its selected value across a repaint.
 *
 * `leading` is the same contract at the HEAD of the row (the image picker's
 * Library/Brand/Stock control, which reads before the categories it narrows).
 * It is a second name rather than a position flag because a row can want both,
 * and because the picker found the trap the hard way: an element merely
 * prepended by a consumer survived exactly until the first chip click.
 */
export function renderChipRow(chipRow, model, opts = {}) {
  const {
    labelOf = slugToLabel,
    countOf,
    activeKey = 'all',
    extra = [],
    drawerEl = null,
    drawerOpen = false,
    leading = null,
    trailing = null
  } = opts;
  chipRow.textContent = '';
  if (leading) chipRow.appendChild(leading);
  // DEDUPED. `extra` is "show this key even at a zero count" and `model.pinned`
  // is "these keys are in the records"; a key that is both is one filter, not
  // two. The image picker hits this on every source that publishes a category
  // list AND stamps those categories onto its records — it was rendering
  // `Gallery 1 · Hero 1 · Navigation 1 · Gallery 1 · Hero 1 · Navigation 1`,
  // six chips for three images. A Set preserves first-occurrence order, so the
  // extras still lead.
  const keys = [...new Set([...extra, ...model.pinned])];
  const buildChip = (key, { drawer = false } = {}) => {
    const label = document.createElement('span');
    label.className = 'pb-coll-chip-label';
    label.textContent = labelOf(key);
    const content = [label];
    const count = typeof countOf === 'function' ? countOf(key) : model.counts.get(key);
    if (count !== undefined && count !== null) {
      const c = document.createElement('span');
      c.className = 'pb-coll-chip-count';
      c.textContent = String(count);
      content.push(c);
    }
    // `bare` — the chip carries its own surface class; it must not inherit .pb-btn.
    const btn = createButton({
      variant: 'bare',
      className: `pb-coll-chip${drawer ? ' pb-coll-chip-drawer' : ''}`,
      content,
      dataset: { cbChip: key }
    });
    btn.setAttribute('aria-pressed', String(key === activeKey));
    return btn;
  };
  for (const key of keys) chipRow.appendChild(buildChip(key));

  if (drawerEl) {
    drawerEl.textContent = '';
    drawerEl.hidden = !drawerOpen || model.overflow.length === 0;
    if (model.overflow.length > 0) {
      const count = document.createElement('span');
      count.className = 'pb-coll-more-count';
      count.textContent = String(model.overflow.length);
      const more = createButton({
        variant: 'bare',
        className: 'pb-coll-more',
        label: 'More',
        content: count,
        ariaExpanded: drawerOpen,
        dataset: { cbDrawerToggle: 'true' }
      });
      chipRow.appendChild(more);
      for (const group of model.groupedOverflow) {
        const groupEl = document.createElement('div');
        groupEl.className = 'pb-coll-drawer-group';
        const letter = document.createElement('div');
        letter.className = 'pb-coll-drawer-letter';
        letter.setAttribute('aria-hidden', 'true');
        letter.textContent = group.letter;
        const items = document.createElement('div');
        items.className = 'pb-coll-drawer-items';
        for (const key of group.keys) items.appendChild(buildChip(key, { drawer: true }));
        groupEl.append(letter, items);
        drawerEl.appendChild(groupEl);
      }
    }
  }
  if (trailing) {
    // Marked, not selected by position. On a phone the row becomes a single
    // horizontal scroller and this element stays pinned to the right edge
    // (collection.css), which needs a stable hook — `:last-child` would be
    // whatever chip happened to render last on a surface that passes no
    // trailing, and would pin a filter chip instead.
    trailing.classList.add('pb-coll-chips-trailing');
    chipRow.appendChild(trailing);
  }
  return chipRow;
}

// ────────────────────────────── the slot queue ───────────────────────────────
//
// DS-1. Structure only, like everything else in this file: `collection-browser`
// wires the click and key handling, and `ui/slot-queue.js` owns the state. The
// three-way split is what let the image picker's queue move house without its
// behaviour moving with it.

/**
 * The queue strip: one chip per slot, then a title and a sub line.
 *
 * @param {{ariaLabel?: string}} [opts]
 * @returns {{el: HTMLElement, strip: HTMLElement, titleEl: HTMLElement, subEl: HTMLElement}}
 */
export function buildQueueStrip({ ariaLabel = 'Slots' } = {}) {
  const el = document.createElement('div');
  el.className = 'pb-coll-queue';

  const strip = document.createElement('div');
  strip.className = 'pb-coll-slots';
  strip.setAttribute('role', 'tablist');
  strip.setAttribute('aria-label', ariaLabel);

  const meta = document.createElement('div');
  meta.className = 'pb-coll-queue-meta';
  const titleEl = document.createElement('p');
  titleEl.className = 'pb-coll-queue-title';
  const subEl = document.createElement('p');
  subEl.className = 'pb-coll-queue-sub';
  meta.append(titleEl, subEl);

  el.append(strip, meta);
  return { el, strip, titleEl, subEl };
}

/**
 * Repaint the strip from queue state.
 *
 * Rebuilt wholesale rather than diffed — at four chips the diff costs more to
 * read than it saves, and the whole row is re-derived from one state.
 *
 * THE ROVING TABINDEX IS SET HERE, not once at setup. That is the MK lesson
 * about `gridKeyNav` verbatim: a one-shot initialiser that reads the DOM must
 * be re-run by whatever WRITES the DOM, and this function is the writer. The
 * donor strip had no tabindex management and no arrow handler at all — it was
 * `role="tablist"` with `role="tab"` children and a keyboard user could reach
 * exactly the first chip, then nothing.
 *
 * @param {{strip: HTMLElement, titleEl: HTMLElement, subEl: HTMLElement}} refs
 * @param {ReturnType<import('./slot-queue.js').createSlotQueue>} queue
 * @param {{title?: string, sub?: string, buildChip: (slot, i) => HTMLElement}} opts
 */
export function paintQueueStrip(refs, queue, { title = '', sub = '', buildChip }) {
  const { entries, cursor } = queue;
  // Hidden below two: a queue of one is a chip that can only be what it
  // already is. The title and sub still paint — a one-slot dialog still has a
  // target to name.
  refs.strip.hidden = entries.length < 2;
  refs.strip.textContent = '';
  if (entries.length >= 2) {
    entries.forEach((slot, i) => {
      const chip = buildChip(slot, i);
      chip.setAttribute('aria-selected', String(i === cursor));
      // Roving: exactly one chip is in the tab order, and it is the current one.
      chip.tabIndex = i === cursor ? 0 : -1;
      if (i === cursor) chip.classList.add('is-current');
      if (slot.filled) chip.classList.add('is-done');
      refs.strip.appendChild(chip);
    });
  }
  refs.titleEl.textContent = title;
  refs.subEl.textContent = sub;
}

/** Paint the empty/filtered-empty/error state and the header meta line. */
export function paintEmptyState({
  emptyEl,
  metaEl,
  listEl,
  shown,
  total,
  query = '',
  chipKey = 'all',
  emptyCopy,
  filteredEmptyCopy,
  errorCopy,
  error = false,
  metaText,
  illustration: sceneName,
  // PP-0: a list whose RECORDS are all gone can still hold a visible tile (a
  // "New project" tile in an empty projects list is the point of the tile).
  // The empty copy still paints beneath it; only the list's own hiding is
  // withheld. Default false, so every existing surface is unchanged.
  keepList = false
}) {
  const isFiltering = !!query || (chipKey && chipKey !== 'all');
  if (metaEl) {
    metaEl.textContent =
      typeof metaText === 'function'
        ? metaText({ shown, total, query, chipKey })
        : isFiltering
          ? `Showing ${shown} of ${total}`
          : total
            ? `${total} total`
            : '';
  }
  const showEmpty = error || shown === 0;
  emptyEl.parentElement?.classList.toggle('pb-coll-scroll-empty', showEmpty);
  emptyEl.hidden = !showEmpty;
  if (listEl) listEl.hidden = showEmpty && total === 0 && !keepList;
  if (!showEmpty) return;
  emptyEl.textContent = '';
  const copy = error ? errorCopy : isFiltering ? filteredEmptyCopy : emptyCopy;
  const {
    title,
    body,
    action,
    illustration: copyScene
  } = typeof copy === 'function' ? copy({ query, chipKey }) : copy || { title: 'Nothing here yet' };
  // Illustration slot (empty-state initiative): a scene from ui/illustrations.js
  // above the title, wearing the badge for WHY the list is empty — the same
  // three-way state this function already computes for the copy, plus 'search'
  // when a query is what filtered everything out. Gated on the surface (or the
  // copy) providing a name, so every existing surface renders byte-identically
  // (same contract as `action` below).
  const scene = copyScene || sceneName;
  if (scene) {
    const variant = error ? 'error' : query ? 'search' : isFiltering ? 'filtered' : 'empty';
    const holder = document.createElement('div');
    holder.className =
      'pb-illo pb-coll-empty-illo' + (scene === 'sections' ? ' pb-illo--section' : '');
    // Trusted static registry markup — the ONE sanctioned raw-innerHTML write
    // here. Must NEVER be routed through escapeHTML (it would render as text).
    holder.innerHTML = illustration(scene, { variant });
    if (holder.firstElementChild) emptyEl.appendChild(holder);
  }
  const titleEl = document.createElement('div');
  titleEl.className = 'pb-coll-empty-title';
  titleEl.textContent = title || (error ? 'Something went wrong' : 'Nothing here yet');
  emptyEl.appendChild(titleEl);
  if (body) {
    const bodyEl = document.createElement('div');
    bodyEl.className = 'pb-coll-empty-body';
    bodyEl.textContent = body;
    emptyEl.appendChild(bodyEl);
  }
  // `action` (CB-12b): an empty state that can name a way OUT of itself. Add
  // Section's industry filter can exclude everything a search would otherwise
  // match, and "Show all industries" is the only affordance that recovers it —
  // an empty state that just says "no results" leaves the user to guess which
  // of two filters did it. Gated on the copy providing one, so every existing
  // surface renders byte-identically.
  if (action && action.label) {
    emptyEl.appendChild(
      createButton({
        variant: action.variant || 'primary',
        size: action.size || 'sm',
        label: action.label,
        className: action.className,
        dataset: action.dataset,
        onClick: action.onClick
      })
    );
  }
}
